import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import ProductTable from "../components/admin/ProductTable";
import ProductForm from "../components/admin/ProductForm";
import WarrantyCard from "../components/card/WarrantyCard";
import { getProducts, getProduct, deleteProduct } from "../utils/api";
import { formatDate } from "../utils/helpers";

export default function Dashboard() {
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [editProduct, setEditProduct] = useState(null);
  const [cardProduct, setCardProduct] = useState(null);
  const [viewProduct, setViewProduct] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchProducts = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await getProducts({ page, search, limit: 15 });
      setProducts(res.data.data);
      setPagination(res.data.pagination);
    } catch {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { fetchProducts(1); }, [fetchProducts]);

  const handleDelete = async () => {
    try {
      await deleteProduct(confirmDelete._id);
      toast.success("Product deleted");
      setConfirmDelete(null);
      fetchProducts(pagination.page);
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleView = async (p) => {
    try {
      const res = await getProduct(p._id);
      setViewProduct(res.data.data);
    } catch {
      toast.error("Failed to load details");
    }
  };

  const stats = {
    total: pagination.total,
    active: products.filter((p) => {
      const exp = new Date(p.purchaseDate);
      exp.setMonth(exp.getMonth() + p.warrantyDurationMonths);
      return new Date() <= exp;
    }).length,
  };

  const modalStyle = { background: "#141414", border: "1px solid rgba(200,150,12,0.25)" };
  const modalHeaderStyle = { borderBottom: "1px solid rgba(200,150,12,0.2)" };

  return (
    <div className="min-h-screen" style={{ background: "#0f0f0f" }}>
      {/* Stats */}
      <div style={{ background: "#111", borderBottom: "1px solid rgba(200,150,12,0.15)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-2xl font-bold mb-4" style={{ color: "#FFD700", fontFamily: "serif", letterSpacing: "0.05em" }}>Dashboard</h1>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { label: "Total Records", value: pagination.total, border: "#C8960C", text: "#FFD700" },
              { label: "Active Warranties", value: stats.active, border: "#4a7c4e", text: "#6fcf7f" },
              { label: "Expired", value: pagination.total - stats.active, border: "#7c4a4a", text: "#cf6f6f" },
            ].map((s) => (
              <div key={s.label} className="rounded-xl p-4" style={{ background: "#1a1a1a", border: `1px solid ${s.border}40` }}>
                <p className="text-xs font-medium uppercase tracking-widest mb-1" style={{ color: s.border }}>{s.label}</p>
                <p className="text-3xl font-black" style={{ color: s.text }}>{s.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        {/* Search */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#8A6000" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search by name, product, or warranty ID…"
              className="input-field pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className="card p-0">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: "#C8960C", borderTopColor: "transparent" }} />
            </div>
          ) : (
            <ProductTable
              products={products}
              onView={handleView}
              onEdit={(p) => setEditProduct(p)}
              onDelete={(p) => setConfirmDelete(p)}
              onGenerateCard={async (p) => {
                const res = await getProduct(p._id);
                setCardProduct(res.data.data);
              }}
            />
          )}
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex justify-center gap-2">
            {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => fetchProducts(p)}
                className="w-9 h-9 rounded-lg text-sm font-medium transition-all"
                style={p === pagination.page
                  ? { background: "rgba(200,150,12,0.2)", color: "#FFD700", border: "1px solid #C8960C" }
                  : { background: "#1a1a1a", color: "#8A6000", border: "1px solid #3a2a00" }
                }
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editProduct && (
        <div className="fixed inset-0 bg-black/70 z-40 flex items-start justify-center p-4 overflow-y-auto">
          <div className="rounded-2xl shadow-2xl w-full max-w-2xl mt-8" style={modalStyle}>
            <div className="flex items-center justify-between p-6" style={modalHeaderStyle}>
              <h2 className="text-lg font-bold" style={{ color: "#FFD700", fontFamily: "serif" }}>Edit Product</h2>
              <button onClick={() => setEditProduct(null)} style={{ color: "#8A6000" }} className="hover:text-yellow-400 text-xl">✕</button>
            </div>
            <div className="p-6">
              <ProductForm
                existing={editProduct}
                onSuccess={() => { setEditProduct(null); fetchProducts(pagination.page); }}
                onCancel={() => setEditProduct(null)}
              />
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewProduct && (
        <div className="fixed inset-0 bg-black/70 z-40 flex items-start justify-center p-4 overflow-y-auto">
          <div className="rounded-2xl shadow-2xl w-full max-w-2xl mt-8" style={modalStyle}>
            <div className="flex items-center justify-between p-6" style={modalHeaderStyle}>
              <h2 className="text-lg font-bold" style={{ color: "#FFD700", fontFamily: "serif" }}>Product Details</h2>
              <button onClick={() => setViewProduct(null)} style={{ color: "#8A6000" }} className="hover:text-yellow-400 text-xl">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                {[
                  ["Warranty ID", viewProduct.warrantyId],
                  ["Customer Name", viewProduct.customerName],
                  ["Phone", viewProduct.phoneNumber],
                  ["Product", viewProduct.productName],
                  ["Purchase Date", formatDate(viewProduct.purchaseDate)],
                  ["Duration", viewProduct.warrantyDuration],
                ].map(([k, v]) => (
                  <div key={k}>
                    <p className="text-xs uppercase tracking-widest mb-0.5" style={{ color: "#8A6000" }}>{k}</p>
                    <p className="font-medium" style={{ color: "#f5f0e8" }}>{v}</p>
                  </div>
                ))}
                <div className="col-span-2">
                  <p className="text-xs uppercase tracking-widest mb-0.5" style={{ color: "#8A6000" }}>Address</p>
                  <p className="font-medium" style={{ color: "#f5f0e8" }}>{viewProduct.address}</p>
                </div>
                {viewProduct.productDescription && (
                  <div className="col-span-2">
                    <p className="text-xs uppercase tracking-widest mb-0.5" style={{ color: "#8A6000" }}>Description</p>
                    <p className="font-medium" style={{ color: "#f5f0e8" }}>{viewProduct.productDescription}</p>
                  </div>
                )}
              </div>
              {viewProduct.images?.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-widest mb-2" style={{ color: "#8A6000" }}>Images</p>
                  <div className="flex gap-2 flex-wrap">
                    {viewProduct.images.map((img) => (
                      <img key={img.filename} src={img.url} alt="" className="w-20 h-20 object-cover rounded-lg" style={{ border: "1px solid rgba(200,150,12,0.3)" }} />
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={() => { setViewProduct(null); setCardProduct(viewProduct); }} className="btn-primary flex-1">
                  Generate Warranty Card
                </button>
                <button onClick={() => setViewProduct(null)} className="btn-secondary">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="rounded-2xl shadow-2xl w-full max-w-sm p-6" style={modalStyle}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(127,29,29,0.3)", border: "1px solid #7f1d1d" }}>
                <svg className="w-5 h-5" style={{ color: "#cf6f6f" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold" style={{ color: "#f5f0e8" }}>Delete Product</h3>
                <p className="text-sm" style={{ color: "#8A6000" }}>This action cannot be undone</p>
              </div>
            </div>
            <p className="text-sm mb-5" style={{ color: "#a09070" }}>
              Are you sure you want to delete <strong style={{ color: "#f5f0e8" }}>{confirmDelete.productName}</strong> for <strong style={{ color: "#f5f0e8" }}>{confirmDelete.customerName}</strong>?
            </p>
            <div className="flex gap-3">
              <button onClick={handleDelete} className="btn-danger flex-1">Delete</button>
              <button onClick={() => setConfirmDelete(null)} className="btn-secondary flex-1">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Warranty Card Modal */}
      {cardProduct && <WarrantyCard product={cardProduct} onClose={() => setCardProduct(null)} />}
    </div>
  );
}
